/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";

import useStyle from "./styles";
import { MESSAGES, TITLE } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { selectError } from "../../store/error/errorSelector";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { showErrorAction } from "../../store/error/errorActionCreator";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { selectLoaderComponent } from "../../store/loader/loaderSelector";
import { selectSelectedApplication } from "../../store/application/applicationSelector";
import { createApplication, editApplication } from "../../store/application/application";
import { selectSelectedOrganization } from "../../store/organization/organizationSelector";

export interface IApplicationData {
  id: string;
  appName: string;
  description: string;
};

interface IAddApplicationDialogProps {
  applicationData?: IApplicationData;
  isEdit: boolean;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
  openModal: boolean;
  refreshOnSave: Function;
  searchString?: string;
};

export interface IApplicationRequest {
  id?: string;
  name?: string;
  description?: string;
  accountKey?: string;
  contactEmail?: string;
};

const formDefaultValues: IApplicationRequest = {
  id: "",
  name: "",
  description: "",
  accountKey: "",
  contactEmail: "",
};

const formErrorDefaultValues = {
  errorName: false,
  errorDescription: false,
  errorAppAccountKey: false,
  errorContactEmail: false,
  errorHelperTextContactEmail: "",
  errorHelperTextAppAccountKey: "",
  errorHelperTextName: "",
  errorHelperTextDescription: "",
};

const AddApplicationDialog = (props: IAddApplicationDialogProps) => {
  const { openModal, openCloseModal } = props;
  const classes = useStyle();
  const dispatch = useDispatch();

  const errorMesaage = useSelector(selectError);
  const loaderComponent = useSelector(selectLoaderComponent);
  const selectedApplication = useSelector(selectSelectedApplication);
  const selectedOrganization = useSelector(selectSelectedOrganization);

  const [showLoader, showHideLoader] = useState(false);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<IApplicationRequest>(formDefaultValues);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.openModal]);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    showHideLoader(false);
    if (props.isEdit) {
      showHideLoader(false);
      updateFormValues({ ...props.applicationData });
    }
  }, [openModal, props.isEdit, props.applicationData]);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    const trimmedValue = value.trim();
    if (name === 'accountKey') {
      let errorObj = { errorAppAccountKey: false, errorHelperTextAppAccountKey: '' };

      if (trimmedValue === '') {
        updateFormValues({ ...formValues, accountKey: '' });
      } else if (trimmedValue.length > 8) {
        errorObj = {
          errorAppAccountKey: true,
          errorHelperTextAppAccountKey: MESSAGES.MAX_APP_ACCOUNT_KEY_LENGTH,
        };
      } else if (!MESSAGES.APP_ACCOUNT_KEY_REGEX.test(trimmedValue)) {
        errorObj = {
          errorAppAccountKey: true,
          errorHelperTextAppAccountKey: MESSAGES.APP_ACCOUNT_KEY_ALLOWED_CHARACTERS,
        };
      } else {
        updateFormValues({ ...formValues, accountKey: trimmedValue });
      }
      updateFormError({ ...formErrorState, ...errorObj });
    } else {
      updateFormValues({ ...formValues, [name]: value });
      updateFormError({ ...formErrorDefaultValues });
    }
  };

  const validateRequired = (values: any) => {
    if (!values.name) {
      updateFormError({
        ...formErrorState,
        errorName: true,
        errorHelperTextName: MESSAGES.APPLICATION_NAME_REQUIRED,
      });
      return false;
    } else if (!values.description) {
      updateFormError({
        ...formErrorState,
        errorDescription: true,
        errorHelperTextDescription: MESSAGES.DESCRIPTION_REQUIRED,
      });
      return false;
    } else if (!values.contactEmail) {
      updateFormError({
        ...formErrorState,
        errorContactEmail: true,
        errorHelperTextContactEmail: MESSAGES.CONTACT_EMAIL_REQUIRED,
      });
      return false;
    } else if (values.contactEmail && !MESSAGES.CONTACT_EMAIL_REGEX_VALIDATION.test(values.contactEmail)) {
      updateFormError({
        ...formErrorState,
        errorContactEmail: true,
        errorHelperTextContactEmail: MESSAGES.CONTACT_EMAIL_INVALID,
      });
      return false;
    } else if (!values.accountKey || !/^[a-z]+$/.test(values.accountKey)) {
      updateFormError({
        ...formErrorState,
        errorAppAccountKey: true,
        errorHelperTextAppAccountKey: MESSAGES.APP_ACCOUNT_KEY_REQUIRED,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    let isSuccess = false;
    if (validateRequired(formValues)) {
      let payload: any = {
        name: formValues.name && formValues?.name.toLowerCase() || '',
        description: formValues.description,
        organizationId: selectedOrganization?.id,
        accountKey: formValues.accountKey || '',
        contactEmail: formValues.contactEmail || '',
        searchString: props.searchString || '',
      };
      showHideLoader(true);
      if (props.isEdit) {
        payload.id = selectedApplication.id;
        if (selectedApplication.accountKey) {
          delete payload.accountKey;
        }
        isSuccess = await editApplication(dispatch, payload);
      } else {
        isSuccess = await createApplication(dispatch, payload);
      }
      showHideLoader(false);
      if (isSuccess) handleClose();
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            variant="outlined"
            onClick={handleClose}
          >
            {TITLE.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            variant="contained"
            disabled={showLoader}
            onClick={handleSubmit}
            name="createCustomerBtn"
          >
            {TITLE.SAVE}
          </Button>
        }
        openModal={openModal}
        handleClose={handleClose}
        dialogTitle={props.isEdit ? TITLE.EDIT_APPLICATION : TITLE.ADD_APPLICATION}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="name"
            name="name"
            variant="standard"
            disabled={showLoader}
            value={formValues.name}
            onChange={updateFormFields}
            error={formErrorState.errorName}
            label={TITLE.APPLICATION_NAME}
            className={classes.modalTextBox}
            helperText={formErrorState.errorHelperTextName}
          />
          <CustomTooltip title={MESSAGES.APPLICATION_NAME_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="description"
            name="description"
            variant="standard"
            disabled={showLoader}
            label={TITLE.DESCRIPTION}
            onChange={updateFormFields}
            value={formValues.description}
            className={classes.modalTextBox}
            error={formErrorState.errorDescription}
            helperText={formErrorState.errorHelperTextDescription}
          />
          <CustomTooltip title={MESSAGES.DESCRIPTION_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="contactEmail"
            name="contactEmail"
            variant="standard"
            disabled={showLoader}
            label={TITLE.CONTACT_EMAIL}
            onChange={updateFormFields}
            value={formValues.contactEmail}
            className={classes.modalTextBox}
            error={formErrorState.errorContactEmail}
            helperText={formErrorState.errorHelperTextContactEmail}
          />
          <CustomTooltip title={MESSAGES.CONTACT_EMAIL_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="accountKey"
            name="accountKey"
            variant="standard"
            onChange={updateFormFields}
            label={TITLE.APP_ACCOUNT_KEY}
            value={formValues.accountKey}
            className={classes.modalTextBox}
            error={formErrorState.errorAppAccountKey}
            helperText={formErrorState.errorHelperTextAppAccountKey}
            disabled={showLoader || (props.isEdit && selectedApplication?.accountKey)}
          />
          <CustomTooltip title={MESSAGES.APP_ACCOUNT_KEY_INFO}></CustomTooltip>
        </Box>
        {/* {errorMesaage && <ErrorMessage message={errorMesaage.message} />} */}
        <Loader insideLoader showLoader={showLoader && (loaderComponent === TITLE.ADD_APPLICATION_DIALOG)} isCenter />
      </FormDialog>
    </Fragment>
  );
};

export default AddApplicationDialog;
